/**
 * Lead Intake Service
 * Handles all API calls for intake forms and leads
 */

import { getAuth } from 'firebase/auth';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://klorah-fast-server-my8l.onrender.com/api';

class LeadIntakeService {
  async getAuthHeaders() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // ============ FORM CRUD ============

  async createForm(formData) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to create form');
    }
    return response.json();
  }

  async getForms(userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/${userId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch forms');
    return response.json();
  }

  async getForm(formId, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/single/${formId}?user_id=${userId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch form');
    return response.json();
  }

  async updateForm(formId, userId, formData) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/${formId}?user_id=${userId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(formData)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to update form');
    }
    return response.json();
  }

  async deleteForm(formId, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/${formId}?user_id=${userId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) throw new Error('Failed to delete form');
    return response.json();
  }

  async publishForm(formId, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/forms/${formId}/publish?user_id=${userId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail?.errors?.join(', ') || error.detail || 'Failed to publish form');
    }
    return response.json();
  }

  // ============ PUBLIC FORM ============

  async getPublicForm(slug) {
    const response = await fetch(`${BASE_URL}/intake/public/${slug}`);
    if (!response.ok) throw new Error('Form not found');
    return response.json();
  }

  async submitForm(formId, responses) {
    const response = await fetch(`${BASE_URL}/intake/submit/${formId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to submit form');
    }
    return response.json();
  }

  // ============ LEAD MANAGEMENT ============

  async getLeads(userId, filters = {}) {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.temperature) params.append('temperature', filters.temperature);
    if (filters.formId) params.append('form_id', filters.formId);

    const response = await fetch(`${BASE_URL}/intake/leads/${userId}?${params}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch leads');
    return response.json();
  }

  async getLead(userId, leadId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/leads/${userId}/${leadId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch lead');
    return response.json();
  }

  async updateLeadStatus(leadId, status, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/leads/${leadId}/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status, user_id: userId })
    });
    if (!response.ok) throw new Error('Failed to update lead status');
    return response.json();
  }

  async sendFollowUp(leadId, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/send-followup/${leadId}?user_id=${userId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) throw new Error('Failed to send follow-up');
    return response.json();
  }

  // ============ SETTINGS ============

  async getSettings(userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/settings/${userId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch settings');
    return response.json();
  }

  async updateSettings(userId, settings) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/intake/settings/${userId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to update settings');
    return response.json();
  }

  // ============ NURTURE SYSTEM ============

  async getNurtureQueue(userId, leadId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/nurture/lead/${leadId}?user_id=${userId}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch nurture queue');
    return response.json();
  }

  async getAllNurtureQueues(userId, status = null) {
    const headers = await this.getAuthHeaders();
    const params = status ? `?status=${status}` : '';
    const response = await fetch(`${BASE_URL}/nurture/queues/${userId}${params}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch nurture queues');
    return response.json();
  }

  async updateNurtureQueueStatus(queueId, userId, status) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/nurture/queue/${queueId}/status?user_id=${userId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update queue status');
    return response.json();
  }

  async sendNextNurtureEmail(queueId, userId) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/nurture/send-now/${queueId}?user_id=${userId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) throw new Error('Failed to send email');
    return response.json();
  }

  async previewNurtureEmail(userId, leadId, stepNumber = 1) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/nurture/preview-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId, lead_id: leadId, step_number: stepNumber })
    });
    if (!response.ok) throw new Error('Failed to generate preview');
    return response.json();
  }

  async updateAISettings(userId, settings) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}/nurture/settings/${userId}/ai`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to update AI settings');
    return response.json();
  }
}

const leadIntakeService = new LeadIntakeService();
export default leadIntakeService;
